import type { Booleans, Pipe, Tuples } from 'hotscript';

import type { TWFontSize } from '../types/tailwind';

export type { TWFontSize };

const withOpacityValue = (variable: string, alphaValue?: number) =>
  `rgb(var(${variable}) / ${alphaValue ?? 1.0})`;

export const defaultPalette = {
  canvas: {
    DEFAULT: withOpacityValue('--colour-canvas'),
  },
  main: {
    // main-XX is deprecated, use main-XXX instead
    DEFAULT: withOpacityValue('--colour-main'),

    // Dark mode compatible grayscale
    200: withOpacityValue('--colour-main-200'),
    300: withOpacityValue('--colour-main-300'),
    400: withOpacityValue('--colour-main-500'),
    500: withOpacityValue('--colour-main-500'),
  },
  negative: withOpacityValue('--colour-negative'),

  // ALIE
  'asset-green': withOpacityValue('--colour-asset-green'),
  'liability-red': withOpacityValue('--colour-liability-red'),
  'income-blue': withOpacityValue('--colour-income-blue'),
  'expense-yellow': withOpacityValue('--colour-expense-yellow'),

  // Code Colours
  amber: withOpacityValue('--colour-amber'),
  orange: withOpacityValue('--colour-orange'),
  royal: withOpacityValue('--colour-royal'),
  forest: withOpacityValue('--colour-forest'),
  purple: withOpacityValue('--colour-purple'),
  magenta: withOpacityValue('--colour-magenta'),

  // Inactive icon quadrant colours
  'inactive-tl': withOpacityValue('--colour-inactive-tl'),
  'inactive-tr': withOpacityValue('--colour-inactive-tr'),
  'inactive-bl': withOpacityValue('--colour-inactive-bl'),
  'inactive-br': withOpacityValue('--colour-inactive-br'),
} as const;

type MainValues = keyof (typeof defaultPalette)['main'];
export type DefaultPalette =
  | `${keyof typeof defaultPalette}`
  | `main-${MainValues}`;

export const fontSizeTailwindConfig = {
  sm: ['14px', '18px'],
  md: ['20px', '27px'],
  lg: ['32px', '36px'],
  xl: ['50px', '63px'],
  xxl: ['64px', '72px'],
} as Record<'sm' | 'md' | 'lg' | 'xl' | 'xxl', [string, string]>;

export type TextSizeType = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export const sizeToTextClassName = {
  sm: 'text-sm',
  md: 'text-md',
  lg: 'text-lg',
  xl: 'text-xl',
  xxl: 'text-xxl',
} as const;

export const sizeToSvgWidth = {
  sm: 12,
  md: 20,
  lg: 32,
  xl: 64,
  xxl: 36,
} as const;

export const fontSizeToIconSize = (size: TextSizeType): TextSizeType => size;

export const colorToHexCode = {
  transparent: 'transparent',
  currentColor: 'currentColor',
  lightgray: '#EBEBEB',
  gray200: 'rgb(var(--colour-main-200))',
  gray400: 'rgb(var(--colour-main-400))',
  gray500: 'rgb(var(--colour-main-500))',
  gray: '#909090',
  darkgray: '#414141',
  black: 'rgb(var(--colour-main))',
  keyline: '#CCCCCC',
  white: 'rgb(var(--colour-negative))',
  orange: 'rgb(var(--colour-orange))',
  forest: '#449A00',
  sea: '#00B292',
  royal: 'rgb(var(--colour-royal))',
  magenta: '#FF00D7',
  'asset-green': 'rgb(var(--colour-asset-green))',
  'liability-red': 'rgb(var(--colour-liability-red))',
  'income-blue': 'rgb(var(--colour-income-blue))',
  'expense-yellow': 'rgb(var(--colour-expense-yellow))',
  amber: '#FF4900',
  purple: '#A53DE7',
  'inactive-tl': 'rgb(var(--colour-inactive-tl))',
  'inactive-tr': 'rgb(var(--colour-inactive-tr))',
  'inactive-bl': 'rgb(var(--colour-inactive-bl))',
  'inactive-br': 'rgb(var(--colour-inactive-br))',
} as const;
export type ColorType = keyof typeof colorToHexCode;

export const colorToTextClassName = {
  transparent: 'text-transparent',
  currentColor: 'text-currentColor',
  orange: 'text-orange',
  forest: 'text-forest',
  sea: 'text-sea',
  gray: 'text-gray',
  gray200: 'text-gray200',
  gray400: 'text-gray400',
  gray500: 'text-gray500',
  black: 'text-black',
  lightgray: 'text-lightgray',
  darkgray: 'text-darkgray',
  keyline: 'text-keyline',
  white: 'text-white',
  royal: 'text-royal',
  magenta: 'text-magenta',
  'asset-green': 'text-asset-green',
  'liability-red': 'text-liability-red',
  'income-blue': 'text-income-blue',
  'expense-yellow': 'text-expense-yellow',
  amber: 'text-amber',
  purple: 'text-purple',
} as const;

export const assetTypeToBackgroundColor = {
  asset: 'asset-green',
  liability: 'liability-red',
  income: 'income-blue',
  expense: 'expense-yellow',
} as const;

export type BaseSvgProps = {
  'data-testid'?: string;
  width?: number;
  backgroundColorHexCode?: string;
  primaryColorHexCode?: string;
  includeSecondaryMark?: boolean;
};

export const AccountTypes = [
  'asset',
  'liability',
  'income',
  'expense',
] as const;
export type AccountType = (typeof AccountTypes)[number];

const GradientColorsNames = [
  'codeblock-copy-texture',
  'sticky-header-background',
  'reverse-sticky-header-background',
] as const;
export type TWGradientColorValues = (typeof GradientColorsNames)[number];

const SpacingNames = [
  'fone-third',
  'ftwo-thirds',
  'fhalf',
  'f0',
  'f1',
  'fone-half',
  'f2',
  'f3',
  'f4',
  'f5',
  'f6',
  'f7',
  'f8',
  'f9',
  'f10',
  'f11',
  'f12',
  'f13',
  'f14',
  'f15',
  'f16',
  'f17',
  'f18',
  'f19',
  'f20',
  'f21',
  'f22',
  'f23',
  'f24',
  'f25',
  'f28',
  'f30',
  'f32',
  'f33',
  'f36',
  'f40',
  'f41',
  'f45',
  'f48',
  'f56',
  'f60',
  'f62',
  'f66',
] as const;
export type TWSpacingValues = (typeof SpacingNames)[number];

export type BaseLineIconProps = {
  type: AccountType;
  size?: TextSizeType;
  disabled?: boolean;
};

export const getTextSize = <T>(size?: TextSizeType): TWFontSize<T> => {
  // must return full string containing complete classname and not concatenate it dynamically
  // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
  let textSize: string;
  switch (size) {
    case 'sm':
      textSize = 'text-sm';
      break;
    case 'md':
      textSize = 'text-md';
      break;
    case 'lg':
      textSize = 'text-lg';
      break;
    case 'xl':
      textSize = 'text-xl';
      break;
    case 'xxl':
      textSize = 'text-xxl';
      break;
    default:
      throw new Error(`getTextSize not implemented for size ${size}`);
  }
  return textSize as TWFontSize<T>;
};
/**
 * Utility type that infers the type of individual elements in
 * a list and reconstructs a list of inferred types.
 *
 * Consider using Join instead.
 */
type Cons<Head, Tail> = Head extends []
  ? []
  : Tail extends []
  ? [Head]
  : Tail extends [infer A, ...infer B]
  ? [Head, ...Cons<A, B>]
  : never;

/**
 * Utility type that returns a list of inferred types of
 * individual elements in a list, or never.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Join<T extends readonly any[]> = T extends readonly [
  infer Head,
  ...infer Tail
]
  ? Cons<Head, Tail>
  : never;

/**
 * Utility type that takes a list of strings and false and returns a
 * space-delimited string containing the list of strings, while discarding
 * the false values.
 */
type StringifyClassNames<T extends readonly (string | false)[]> = Pipe<
  Join<T>,
  [Tuples.Filter<Booleans.DoesNotExtend<false>>, Tuples.Join<' '>]
>;

type StringifyClassNamesResult<T extends readonly (string | false)[] | string> =
  T extends readonly (string | false)[] ? StringifyClassNames<T> : T;

export const strictClassNames = <
  T extends readonly (string | false)[] | string
>(
  args: T
): StringifyClassNamesResult<T> => {
  if (Array.isArray(args)) {
    return args.filter((a) => !!a).join(' ') as StringifyClassNamesResult<T>;
  }
  if (typeof args === 'string') {
    return args as StringifyClassNamesResult<T>;
  }
  throw new Error('Unreachable code');
};
